'use client';

import { createClient } from '@/lib/supabase/client';
import { sendAlertEscalationEmail } from './clinicalEmailService';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ThresholdCondition = 'gt' | 'lt' | 'drop_pct' | 'rise_pct';
export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface AlertThreshold {
  id: string;
  clinician_id: string;
  patient_id?: string | null;
  metric: string;
  condition: ThresholdCondition;
  threshold_value: number;
  severity: AlertSeverity;
  notify_email: boolean;
  notify_inapp: boolean;
  is_active: boolean;
  label?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CognitiveAlertEvent {
  id: string;
  threshold_id?: string | null;
  clinician_id: string;
  patient_id?: string | null;
  metric: string;
  current_value: number;
  baseline_value?: number | null;
  deviation_pct?: number | null;
  severity: AlertSeverity;
  message: string;
  is_read: boolean;
  email_sent: boolean;
  session_id?: string | null;
  triggered_at: string;
}

export interface MetricSnapshot {
  focusScore?: number;
  stressLevel?: number;
  attentionScore?: number;
  thetaAlphaRatio?: number;
  thetaBetaRatio?: number;
  coherence?: number;
  mentalEnergy?: number;
  stability?: number;
  flexibility?: number;
}

// ─── Default Threshold Templates ─────────────────────────────────────────────

export const DEFAULT_THRESHOLD_TEMPLATES: Omit<AlertThreshold, 'id' | 'clinician_id' | 'patient_id' | 'created_at' | 'updated_at'>[] = [
  {
    metric: 'focusScore',
    condition: 'drop_pct',
    threshold_value: 20,
    severity: 'warning',
    notify_email: true,
    notify_inapp: true,
    is_active: true,
    label: 'Focus Drop >20%',
  },
  {
    metric: 'focusScore',
    condition: 'drop_pct',
    threshold_value: 35,
    severity: 'critical',
    notify_email: true,
    notify_inapp: true,
    is_active: true,
    label: 'Critical Focus Drop >35%',
  },
  {
    metric: 'stressLevel',
    condition: 'rise_pct',
    threshold_value: 25,
    severity: 'warning',
    notify_email: true,
    notify_inapp: true,
    is_active: true,
    label: 'Stress Elevation >25%',
  },
  {
    metric: 'stressLevel',
    condition: 'gt',
    threshold_value: 80,
    severity: 'critical',
    notify_email: true,
    notify_inapp: true,
    is_active: true,
    label: 'High Stress >80',
  },
  {
    metric: 'thetaAlphaRatio',
    condition: 'gt',
    threshold_value: 2.0,
    severity: 'warning',
    notify_email: false,
    notify_inapp: true,
    is_active: true,
    label: 'Elevated TAR >2.0',
  },
  {
    metric: 'attentionScore',
    condition: 'lt',
    threshold_value: 35,
    severity: 'warning',
    notify_email: false,
    notify_inapp: true,
    is_active: true,
    label: 'Low Attention <35',
  },
];

export const METRIC_LABELS: Record<string, string> = {
  focusScore: 'Focus Score',
  stressLevel: 'Stress Level',
  attentionScore: 'Attention Score',
  thetaAlphaRatio: 'Theta/Alpha Ratio',
  thetaBetaRatio: 'Theta/Beta Ratio',
  coherence: 'Inter-Hemispheric Coherence',
  mentalEnergy: 'Mental Energy',
  stability: 'Stability',
  flexibility: 'Flexibility',
};

export const CONDITION_LABELS: Record<ThresholdCondition, string> = {
  gt: 'Greater than',
  lt: 'Less than',
  drop_pct: 'Drops by % from baseline',
  rise_pct: 'Rises by % from baseline',
};

// ─── CRUD ─────────────────────────────────────────────────────────────────────

export async function getAlertThresholds(clinicianId: string, patientId?: string): Promise<AlertThreshold[]> {
  const supabase = createClient();
  let query = supabase
    .from('alert_thresholds')
    .select('*')
    .eq('clinician_id', clinicianId)
    .order('created_at', { ascending: false });

  if (patientId) {
    query = query.or(`patient_id.eq.${patientId},patient_id.is.null`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as AlertThreshold[];
}

export async function createAlertThreshold(
  threshold: Omit<AlertThreshold, 'id' | 'created_at' | 'updated_at'>
): Promise<AlertThreshold> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('alert_thresholds')
    .insert(threshold)
    .select()
    .single();
  if (error) throw error;
  return data as AlertThreshold;
}

export async function updateAlertThreshold(
  id: string,
  updates: Partial<Omit<AlertThreshold, 'id' | 'clinician_id' | 'created_at'>>
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('alert_thresholds')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteAlertThreshold(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('alert_thresholds').delete().eq('id', id);
  if (error) throw error;
}

// ─── Alert Events ─────────────────────────────────────────────────────────────

export async function getAlertEvents(
  clinicianId: string,
  options?: { unreadOnly?: boolean; patientId?: string; limit?: number }
): Promise<CognitiveAlertEvent[]> {
  const supabase = createClient();
  let query = supabase
    .from('cognitive_alert_events')
    .select('*')
    .eq('clinician_id', clinicianId)
    .order('triggered_at', { ascending: false })
    .limit(options?.limit ?? 50);

  if (options?.unreadOnly) query = query.eq('is_read', false);
  if (options?.patientId) query = query.eq('patient_id', options.patientId);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as CognitiveAlertEvent[];
}

export async function markAlertRead(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('cognitive_alert_events')
    .update({ is_read: true })
    .eq('id', id);
  if (error) throw error;
}

export async function markAllAlertsRead(clinicianId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('cognitive_alert_events')
    .update({ is_read: true })
    .eq('clinician_id', clinicianId)
    .eq('is_read', false);
  if (error) throw error;
}

async function insertAlertEvent(event: Omit<CognitiveAlertEvent, 'id' | 'triggered_at'>): Promise<CognitiveAlertEvent | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('cognitive_alert_events')
    .insert(event)
    .select()
    .single();
  if (error) {
    console.error('[AlertThreshold] Failed to insert alert event:', error.message);
    return null;
  }
  return data as CognitiveAlertEvent;
}

// ─── Evaluation Engine ────────────────────────────────────────────────────────

function evaluateCondition(
  condition: ThresholdCondition,
  currentValue: number,
  thresholdValue: number,
  baselineValue?: number
): boolean {
  switch (condition) {
    case 'gt':
      return currentValue > thresholdValue;
    case 'lt':
      return currentValue < thresholdValue;
    case 'drop_pct': {
      if (baselineValue == null || baselineValue === 0) return false;
      const drop = ((baselineValue - currentValue) / baselineValue) * 100;
      return drop >= thresholdValue;
    }
    case 'rise_pct': {
      if (baselineValue == null || baselineValue === 0) return false;
      const rise = ((currentValue - baselineValue) / baselineValue) * 100;
      return rise >= thresholdValue;
    }
    default:
      return false;
  }
}

function buildAlertMessage(
  threshold: AlertThreshold,
  currentValue: number,
  baselineValue?: number
): string {
  const metricLabel = METRIC_LABELS[threshold.metric] ?? threshold.metric;
  const condLabel = CONDITION_LABELS[threshold.condition];

  if (threshold.condition === 'drop_pct' && baselineValue != null) {
    const drop = ((baselineValue - currentValue) / baselineValue) * 100;
    return `${metricLabel} dropped ${drop.toFixed(1)}% from baseline (${baselineValue.toFixed(1)} → ${currentValue.toFixed(1)})`;
  }
  if (threshold.condition === 'rise_pct' && baselineValue != null) {
    const rise = ((currentValue - baselineValue) / baselineValue) * 100;
    return `${metricLabel} elevated ${rise.toFixed(1)}% above baseline (${baselineValue.toFixed(1)} → ${currentValue.toFixed(1)})`;
  }
  return `${metricLabel} ${condLabel.toLowerCase()} ${threshold.threshold_value} — current: ${currentValue.toFixed(1)}`;
}

export interface EvaluationContext {
  clinicianId: string;
  patientId?: string;
  patientName?: string;
  clinicianEmail?: string;
  sessionId?: string;
  metrics: MetricSnapshot;
  baseline?: MetricSnapshot;
}

/**
 * Evaluate all active thresholds against current metrics.
 * Fires in-app events and optionally sends email notifications.
 */
export async function evaluateThresholds(ctx: EvaluationContext): Promise<CognitiveAlertEvent[]> {
  const supabase = createClient();

  // Fetch active thresholds for this clinician (global + patient-specific)
  let query = supabase
    .from('alert_thresholds')
    .select('*')
    .eq('clinician_id', ctx.clinicianId)
    .eq('is_active', true);

  if (ctx.patientId) {
    query = query.or(`patient_id.eq.${ctx.patientId},patient_id.is.null`);
  } else {
    query = query.is('patient_id', null);
  }

  const { data: thresholds, error } = await query;
  if (error || !thresholds) return [];

  const firedEvents: CognitiveAlertEvent[] = [];

  for (const t of thresholds as AlertThreshold[]) {
    const currentValue = (ctx.metrics as Record<string, number | undefined>)[t.metric];
    if (currentValue == null) continue;

    const baselineValue = ctx.baseline
      ? (ctx.baseline as Record<string, number | undefined>)[t.metric]
      : undefined;

    const triggered = evaluateCondition(t.condition, currentValue, t.threshold_value, baselineValue);
    if (!triggered) continue;

    const deviationPct =
      baselineValue != null && baselineValue !== 0
        ? ((currentValue - baselineValue) / baselineValue) * 100
        : null;

    const message = buildAlertMessage(t, currentValue, baselineValue ?? undefined);

    const eventPayload: Omit<CognitiveAlertEvent, 'id' | 'triggered_at'> = {
      threshold_id: t.id,
      clinician_id: ctx.clinicianId,
      patient_id: ctx.patientId ?? null,
      metric: t.metric,
      current_value: currentValue,
      baseline_value: baselineValue ?? null,
      deviation_pct: deviationPct,
      severity: t.severity,
      message,
      is_read: false,
      email_sent: false,
      session_id: ctx.sessionId ?? null,
    };

    if (t.notify_inapp) {
      const saved = await insertAlertEvent(eventPayload);
      if (saved) firedEvents.push(saved);
    }

    if (t.notify_email && ctx.clinicianEmail) {
      try {
        await sendAlertEscalationEmail({
          to: ctx.clinicianEmail,
          alertId: t.id,
          patientName: ctx.patientName ?? 'Unknown Patient',
          metric: METRIC_LABELS[t.metric] ?? t.metric,
          value: currentValue.toFixed(2),
          threshold: `${CONDITION_LABELS[t.condition]} ${t.threshold_value}`,
          severity: t.severity === 'info' ? 'info' : t.severity,
          clinicalNote: message,
          actionRequired: t.label ?? 'Review patient cognitive state',
          escalatedBy: 'Automated Alert System',
          sessionId: ctx.sessionId,
        });
        // Mark email sent
        if (firedEvents.length > 0) {
          const lastEvent = firedEvents[firedEvents.length - 1];
          await supabase
            .from('cognitive_alert_events')
            .update({ email_sent: true })
            .eq('id', lastEvent.id);
        }
      } catch (e) {
        console.error('[AlertThreshold] Email send failed:', e);
      }
    }
  }

  return firedEvents;
}
